/**
 * Post Show Listener
 *
 * Registers delegated listeners used on the post detail page and modal flows.
 */

import { getEventScope } from "../../core/event-scope/event-scope";
import { Roles } from "../../constants/roles";
import type { EventRoot } from "../../core/event-scope/event-scope-types";
import type { PostShowEventHandler } from "./post-show-types";
import { postShowForm } from "./post-show-form";

class PostShowListener {
    public static _handlerByRoot = new WeakMap<EventRoot, PostShowEventHandler>();
    public handler: PostShowEventHandler;

    public constructor(handler: PostShowEventHandler) {
        this.handler = handler;
    }

    public setup(root: EventRoot = document): void {
        PostShowListener._handlerByRoot.set(root, this.handler);

        const scope = getEventScope(root, "post-show");

        scope.once("click-handlers", () => {
            const getHandler = (): PostShowEventHandler | undefined => PostShowListener._handlerByRoot.get(root);

            const ignoreIfModalEvent = (matched: Element): boolean => {
                if (root instanceof Document && postShowForm.getClosestElementModalOverlay(matched)) {
                    return true;
                }
                return false;
            };

            scope.delegate("click", `[data-role="${Roles.POST_EDIT_SAVE}"]`, (event: Event, matched: Element) => {
                if (ignoreIfModalEvent(matched)) return;
                getHandler()?.handleSaveEdit(event);
            });

            scope.delegate("click", '[data-action="scroll-comments"]', (event: Event, matched: Element) => {
                if (ignoreIfModalEvent(matched)) return;
                getHandler()?.handleCommentScroll(event);
            });

            scope.delegate("click", `[data-role="${Roles.POST_EDIT_CANCEL}"]`, (event: Event, matched: Element) => {
                if (ignoreIfModalEvent(matched)) return;
                getHandler()?.handleCancelEdit(event);
            });

            scope.delegate(
                "click",
                `[data-role="${Roles.POST_ACTION_EDIT}"][data-post-id]`,
                (event: Event, matched: Element) => {
                    if (ignoreIfModalEvent(matched)) return;
                    getHandler()?.handleEdit(event);
                },
            );

            scope.delegate(
                "click",
                `[data-role="${Roles.POST_ACTION_DELETE}"][data-post-id]`,
                (event: Event, matched: Element) => {
                    if (ignoreIfModalEvent(matched)) return;
                    getHandler()?.handleDelete(event);
                },
            );

            scope.delegate(
                "click",
                `[data-role="${Roles.POST_ACTION_SHARE}"][data-post-id]`,
                (event: Event, matched: Element) => {
                    if (ignoreIfModalEvent(matched)) return;
                    getHandler()?.handleShare(event);
                },
            );

        });
    }
}

export function bindPostShowPageReadScroll(onScroll: () => void): void {
    window.addEventListener("scroll", onScroll, { passive: true });
}

export function unbindPostShowPageReadScroll(onScroll: () => void): void {
    window.removeEventListener("scroll", onScroll);
}

export { PostShowListener };
